import {
  Directive,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { StickyBottomBarPortalService } from '../services/sticky-bottom-bar-portal.service';

/**
 * Teleports the host element into the nearest `umssStickyBottomBarOutlet`
 * slot at runtime (portal pattern).
 *
 * The directive also sets `flex-shrink: 0` on the host so it sticks to the
 * bottom of the outlet's flex-column container.
 *
 * Usage — place the directive on any element/component and it will be
 * moved to the outlet registered in the current layout:
 * ```html
 * <umss-my-bar umssStickyBottomBar />
 * ```
 *
 * The element's Angular component tree (change detection, inputs, outputs)
 * is unaffected — only the DOM position changes.
 */
@Directive({
  selector: '[umssStickyBottomBar]',
  standalone: true,
  host: { '[style.flex-shrink]': '"0"' }
})
export class UmssStickyBottomBarDirective implements OnInit, OnDestroy {
  private el = inject(ElementRef<HTMLElement>);
  private renderer = inject(Renderer2);
  private service = inject(StickyBottomBarPortalService);

  private originalParent: HTMLElement | null = null;
  private originalNextSibling: Node | null = null;

  ngOnInit(): void {
    const outlet = this.service.getOutlet();
    if (!outlet) return;

    const host = this.el.nativeElement;
    this.originalParent = host.parentElement;
    this.originalNextSibling = host.nextSibling;

    this.renderer.appendChild(outlet, host);
  }

  ngOnDestroy(): void {
    if (!this.originalParent?.isConnected) return;

    const host = this.el.nativeElement;
    if (
      this.originalNextSibling &&
      this.originalNextSibling.parentNode === this.originalParent
    ) {
      this.renderer.insertBefore(
        this.originalParent,
        host,
        this.originalNextSibling
      );
    } else {
      this.renderer.appendChild(this.originalParent, host);
    }
  }
}
